import { useCallback, useEffect, useMemo, useSyncExternalStore } from "react"
import { useLocation } from "react-router-dom"
import {
  DEFAULT_APPOINTMENT_SCENARIO,
  getScenarioData,
  isAppointmentScenario,
  type AppointmentScenario
} from "./scenarios"
import type { AppointmentsPageData } from "../types"

/** `?apptState=<scenario>` — the only control for switching states. There is no UI. */
export const APPOINTMENT_SCENARIO_QUERY_PARAM = "apptState"

/**
 * Mirrors what a React Query hook will hand back once the API exists, so swapping the mock for
 * tRPC is a one-file change rather than a reshape of every consumer. The mock resolves
 * synchronously, so `isLoading` is always false and `isError` never fires — there is deliberately
 * no loading or error UI to go with them.
 */
export type MockAppointmentsResult = {
  data: AppointmentsPageData
  isLoading: boolean
  isError: boolean
}

/**
 * Mirrors a React Query *mutation* result, for the same reason as above. The mock cannot fail and
 * settles in the same tick, so there is no pending or failure path — and therefore no retry or
 * error UI in the cancel dialog either.
 */
export type MockCancelAppointmentResult = {
  mutate: (appointmentId: string) => void
  isLoading: boolean
  isError: boolean
}

/**
 * What the member has cancelled, and which scenario they cancelled it in.
 *
 * The fixtures are derived from the scenario on every read, so they have no notion of a mutation;
 * this is the one piece of state that has to outlive them. It sits in a module-level store read
 * through `useSyncExternalStore` — the shape `src/services/help-center/helpCenterPreviewStore.ts`
 * already uses — rather than in a component, because the cancel dialog performs the mutation while
 * the page reads the result, and the two are siblings.
 *
 * In memory only, so a reload puts the fixture back — same as the scenario, which is read fresh from
 * the URL on every render.
 */
type AppointmentCancelations = {
  scenario: AppointmentScenario | null
  canceledAppointmentIds: ReadonlySet<string>
}

/** A module constant, so `getSnapshot` returns a stable reference and cannot loop the renderer. */
const NO_CANCELATIONS: ReadonlySet<string> = new Set()

const NOTHING_CANCELED: AppointmentCancelations = { scenario: null, canceledAppointmentIds: NO_CANCELATIONS }

let cancelations: AppointmentCancelations = NOTHING_CANCELED
const cancelationListeners = new Set<() => void>()

const emitCancelations = (): void => {
  cancelationListeners.forEach((listener) => listener())
}

const subscribeToCancelations = (listener: () => void): (() => void) => {
  cancelationListeners.add(listener)
  return () => {
    cancelationListeners.delete(listener)
  }
}

/**
 * Keyed on the scenario, so a cancelation cannot leak into a state the member switched to. The
 * comparison happens on the read path rather than in an effect because an effect runs *after* the
 * render that would already have applied it.
 */
const getCanceledAppointmentIds = (scenario: AppointmentScenario): ReadonlySet<string> =>
  cancelations.scenario === scenario ? cancelations.canceledAppointmentIds : NO_CANCELATIONS

const cancelAppointment = (scenario: AppointmentScenario, appointmentId: string): void => {
  const canceledAppointmentIds = new Set(getCanceledAppointmentIds(scenario))
  canceledAppointmentIds.add(appointmentId)
  cancelations = { scenario, canceledAppointmentIds }
  emitCancelations()
}

/** Drops cancelations recorded against any other scenario, so `?apptState=` is a clean reset. */
const discardCancelationsOutside = (scenario: AppointmentScenario): void => {
  if (cancelations.scenario === null || cancelations.scenario === scenario) {
    return
  }
  cancelations = NOTHING_CANCELED
  emitCancelations()
}

/** Module state outlives a test; this puts it back. */
export const resetMockAppointmentCancelations = (): void => {
  if (cancelations === NOTHING_CANCELED) {
    return
  }
  cancelations = NOTHING_CANCELED
  emitCancelations()
}

/**
 * Which fixture the page is showing: whatever `?apptState=` names, else the default. An unrecognized
 * name falls through to the same path as no name at all rather than throwing.
 *
 * The URL is the only source. It was also persisted to `localStorage` so the scenario survived an
 * in-app navigation that dropped the param, which made a bare `/appointments` show whichever frame
 * had been visited last — surprising when the URL says nothing about it, and awkward to clear while
 * working through the frames. Reading only the URL makes every state a link you can share and go
 * back to.
 *
 * Shared by both hooks below so they can never disagree about which scenario is current — the
 * mutation would otherwise be able to record a cancelation against a scenario the page is not on.
 */
function useAppointmentScenario(): AppointmentScenario {
  const { search } = useLocation()

  const requested = new URLSearchParams(search).get(APPOINTMENT_SCENARIO_QUERY_PARAM)
  return isAppointmentScenario(requested) ? requested : DEFAULT_APPOINTMENT_SCENARIO
}

export function useMockAppointments(): MockAppointmentsResult {
  const scenario = useAppointmentScenario()
  const canceledAppointmentIds = useSyncExternalStore(subscribeToCancelations, () =>
    getCanceledAppointmentIds(scenario)
  )

  // Switching scenarios throws the cancelation away rather than leaving it to be resurrected by
  // switching back. The read above already ignores it, so this only prunes the store.
  useEffect(() => {
    discardCancelationsOutside(scenario)
  }, [scenario])

  // Memoized so the fixtures' relative dates are read once per scenario instead of drifting on
  // every render.
  const data = useMemo(() => {
    const scenarioData = getScenarioData(scenario)

    // Cancelling **removes** the appointment; it does not move it to Past. Frames 5 and 8 of the
    // design draw the identical Past rows, and the visit that was just cancelled appears in
    // neither — so `past` is passed through untouched, and no `status: "canceled"` record is left
    // behind in the upcoming slot.
    return scenarioData.upcoming && canceledAppointmentIds.has(scenarioData.upcoming.id)
      ? { ...scenarioData, upcoming: null }
      : scenarioData
  }, [scenario, canceledAppointmentIds])

  return { data, isLoading: false, isError: false }
}

/**
 * Cancels an appointment out of the mock. Separate from `useMockAppointments` so the query hook's
 * shape stays exactly what the real query will return, and this one stays exactly what the real
 * mutation will return.
 */
export function useCancelAppointment(): MockCancelAppointmentResult {
  const scenario = useAppointmentScenario()

  const mutate = useCallback((appointmentId: string) => cancelAppointment(scenario, appointmentId), [scenario])

  return { mutate, isLoading: false, isError: false }
}
